import { API_BASE_URL } from './api';

// Helper function to handle API responses
const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json().catch(() => ({} as T));
  if (!response.ok) {
    const error = (data && data.message) || response.statusText;
    throw new Error(error);
  }
  return data as T;
};

export interface Setting {
  setting_id: number;
  setting_name: string;
  setting_value: string;
  status: 'Active' | 'Inactive' | 'Delete';
  created_at: string;
  updated_at: string;
}

export const settingsService = {
  async getSettings(): Promise<Setting[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch settings');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error in getSettings:', error);
      throw error instanceof Error ? error : new Error('Failed to fetch settings');
    }
  },

  async updateSetting(settingId: number, value: string): Promise<Setting> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/${settingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ setting_value: value }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update setting');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error in updateSetting:', error);
      throw error instanceof Error ? error : new Error('Failed to update setting');
    }
  },

  async getAdminCommission(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/adminCommission`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch admin commission');
      }
      
      const setting = await response.json();
      return parseFloat(setting.data.adminCommissionSettings || '0');
    } catch (error) {
      console.error('Error in getAdminCommission:', error);
      return 0; // Return default value if there's an error
    }
  },

  async updateAdminCommission(commission: number): Promise<Setting> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/updateadmincommission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ commission }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update admin commission');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error in updateAdminCommission:', error);
      throw error instanceof Error ? error : new Error('Failed to update admin commission');
    }
  },

  async getWasherCharges(): Promise<number> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/washer-charges`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch washer charges');
      }

      const data = await response.json();
      return Number(data?.data?.washer_charges ?? 0);
    } catch (error) {
      console.error('Error in getWasherCharges:', error);
      return 0;
    }
  },

  async updateWasherCharges(value: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/washer-charges`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ washer_charges: value }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update washer charges');
      }
    } catch (error) {
      console.error('Error in updateWasherCharges:', error);
      throw error instanceof Error ? error : new Error('Failed to update washer charges');
    }
  },
};
